import React from 'react'

// Hooks
import { useUpdateShareParentPermissions } from 'modules/shares/modals/ShareSettingsModal/ParentPermissions/useUpdateShareParentPermissions'

// Components
import { Banner, Box } from '@sketch/components'
import { ParentAccessLevel } from '@sketch/modules-common'
import InvitePeople from 'modules/shares/modals/ShareSettingsModal/InvitePeople'
import ShareMemberships from 'modules/shares/modals/ShareSettingsModal/ShareMemberships'

// Styles
import { ShareParentName, StyledRadioButton } from './SharingModal.styles'

// Types
import { ShareWithoutVersion } from 'modules/versioning'
import {
  RadioLabel,
  FormLabel,
  StyledSeparator,
} from 'modules/projects/modals/NewProjectSharingModal/Workspace/WorkspacePanel.styles'

export const WorkspaceMembersTitle = ({
  share,
}: {
  share: Pick<ShareWithoutVersion, 'project' | 'parentAccessLevel'>
}) => {
  const viewOnly = share.parentAccessLevel === 'VIEW'
  const inRestrictedProject = share.project?.workspaceAccessLevel === 'NONE'
  const restricted = viewOnly || inRestrictedProject

  return <>Workspace Members ({restricted ? 'Restricted' : 'All'})</>
}

export const RestrictedProjectCallout = ({
  share,
}: {
  share: Pick<ShareWithoutVersion, 'project' | 'isDraft'>
}) => {
  const inRestrictedProject = share.project?.workspaceAccessLevel === 'NONE'
  const isDraft = share.project?.type === 'PERSONAL_DRAFTS'

  if (!inRestrictedProject) {
    return null
  }

  const calloutCopy = isDraft
    ? 'You can share documents in My Drafts with specific people, but not the whole Workspace.'
    : 'This document is in a restricted project. Changing access options will only affect current project Members.'

  return (
    <Box mb={6}>
      <Banner
        type="information"
        data-testid="sharing-workspace-restricted-banner"
      >
        {calloutCopy}
      </Banner>
    </Box>
  )
}

export const WorkspaceMembersPanel = ({
  share,
}: {
  share: ShareWithoutVersion
}) => {
  const {
    getUpdateShareParentPermissionsDropdownProps,
    shareParentAccessLevel,
  } = useUpdateShareParentPermissions({ share })

  const { setLevel } = getUpdateShareParentPermissionsDropdownProps()

  const viewOnly = shareParentAccessLevel === 'VIEW'

  const inRestrictedProject = share.project?.workspaceAccessLevel === 'NONE'

  const workspaceName = share.workspace.name
  const projectName = share.project?.name
  const parentName = inRestrictedProject ? projectName : workspaceName

  const handleAccessLevelChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setLevel(e.target.value as ParentAccessLevel)

  const isDraft = !!(share.isDraft ?? share.project?.type === 'PERSONAL_DRAFTS')

  const shareWithIsDraft = { ...share, isDraft }

  return (
    <>
      <RestrictedProjectCallout share={share} />

      {!isDraft && (
        <>
          <RadioLabel>
            By default, Members of “
            <ShareParentName>{parentName}</ShareParentName>” can:
          </RadioLabel>

          <StyledRadioButton
            name="accessLevel"
            label="Edit or view this document"
            checked={!viewOnly}
            value="DEFAULT"
            onChange={handleAccessLevelChange}
          />
          <StyledRadioButton
            name="accessLevel"
            label="View this document on the web app"
            checked={viewOnly}
            value="VIEW"
            onChange={handleAccessLevelChange}
          />
        </>
      )}

      <StyledSeparator />

      <FormLabel>
        Add specific “<ShareParentName>{workspaceName}</ShareParentName>”
        Members:
      </FormLabel>
      <InvitePeople
        data-testid="sharing-workspace-invite"
        hideLabel
        share={share}
        filterMembers="Members"
      />
      <ShareMemberships share={shareWithIsDraft} excludeGuests={true} />
    </>
  )
}
